import { ClickAwayListener, FormControl, Typography } from '@mui/material';
import CloseMIcon from 'crono-fe-common/icons/Icon-Close';
import { colors } from 'crono-fe-common/theme';
import TagContainerComponent, {
  TagFilterTooltip,
} from 'pages/templates/components/tagTooltip';
import { useMemo, useState } from 'react';
import { ReactComponent as TopArrowIcon } from 'crono-fe-common/icons/Top-Arrow.svg';
import { ReactComponent as BottomArrowIcon } from 'crono-fe-common/icons/Bottom-Arrow.svg';
import useGetExternalProperty from 'hooks/services/externalProperty/useGetExternalProperty';
import ExternalPropertyFilter from 'crono-fe-common/types/externalPropertyFilter';
import { externalPropertiesToTagInsert } from 'crono-fe-common/utils';
import styled from '@emotion/styled';

interface IProps {
  filterExternalProperties: ExternalPropertyFilter[];
  setFilterExternalProperties: (value: ExternalPropertyFilter[]) => void;
  shortSelectedLabel?: boolean;
  customWidth?: number;
}

const TagsFilterComponent = ({
  filterExternalProperties,
  setFilterExternalProperties,
  shortSelectedLabel = false,
  customWidth,
}: IProps) => {
  const [tooltipToggle, setTooltipToggle] = useState(false);
  const { data: externalProperties } = useGetExternalProperty({
    tableType: null,
    onlyTag: true,
  });
  const flatTagsFilter = useMemo(() => {
    return externalPropertiesToTagInsert(filterExternalProperties);
  }, [filterExternalProperties]);
  // When a property is pressed I update the filters
  const handleChangeExternalProperty = (
    externalPropertyId: number,
    option: string,
  ) => {
    // If the option is not selected, I add it to the filters
    if (
      !filterExternalProperties
        ?.filter((ep) => ep.externalPropertyId === externalPropertyId)
        .at(0)
        ?.values?.includes(option)
    ) {
      const externalProperty =
        filterExternalProperties?.filter(
          (ep) => ep.externalPropertyId === externalPropertyId,
        )?.[0] || ({ externalPropertyId } as ExternalPropertyFilter);
      externalProperty.values = [...(externalProperty.values ?? []), option];
      const newExternalProperties: ExternalPropertyFilter[] = [
        ...(filterExternalProperties?.filter(
          (ep) => ep.externalPropertyId !== externalPropertyId,
        ) || []),
        externalProperty,
      ];

      setFilterExternalProperties([...newExternalProperties]);
    } else {
      //Remove the filter
      const externalProperty: ExternalPropertyFilter =
        filterExternalProperties?.filter(
          (ep) => ep.externalPropertyId === externalPropertyId,
        )?.[0] || ({ externalPropertyId } as ExternalPropertyFilter);
      externalProperty.values = externalProperty.values?.filter(
        (value) => value !== option,
      );
      // If the externalProperty has no value I have to remove it from the filters (not adding it)
      const newExternalProperties: ExternalPropertyFilter[] = [
        ...(filterExternalProperties?.filter(
          (ep) => ep.externalPropertyId !== externalPropertyId,
        ) || []),
      ];
      if (externalProperty.values && externalProperty.values?.length > 0) {
        newExternalProperties.push(externalProperty);
      }
      setFilterExternalProperties([...newExternalProperties]);
    }
  };
  return (externalProperties?.data?.data.length ?? 0) > 0 ? (
    <ClickAwayListener onClickAway={() => setTooltipToggle(false)}>
      <FormControl
        sx={{
          mt: 1,
          mb: 1,
          height: '40px',
        }}
        size="small"
      >
        <TagFilterTooltip
          open={tooltipToggle}
          title={
            <TagContainerComponent
              externalProperties={
                externalProperties?.data?.data.filter(
                  (property) => property.isTag,
                ) || []
              }
              filterExternalProperties={filterExternalProperties}
              handleChangeExternalProperty={handleChangeExternalProperty}
            />
          }
          sx={{
            zIndex: '35002 !important',
          }}
        >
          {flatTagsFilter?.length ? (
            <TagsFilterComponentWrapper
              className="tags-selected"
              onClick={() => setTooltipToggle(!tooltipToggle)}
              customWidth={customWidth}
            >
              <Typography fontSize={'14px'} style={{ flex: 1 }} noWrap>
                Tags ({flatTagsFilter.length}
                {shortSelectedLabel ? '' : ' selected'})
              </Typography>

              <CloseMIcon
                className="remove-text-icon"
                color={colors.grey11}
                onClick={() => {
                  setFilterExternalProperties([]);
                }}
              />
            </TagsFilterComponentWrapper>
          ) : (
            <TagsFilterComponentWrapper
              onClick={() => setTooltipToggle(!tooltipToggle)}
              customWidth={customWidth}
            >
              <Typography fontSize={'14px'} style={{ flex: 1 }} noWrap>
                Tags
              </Typography>
              {tooltipToggle ? (
                <TopArrowIcon className="tag-arrow" />
              ) : (
                <BottomArrowIcon className="tag-arrow" />
              )}
            </TagsFilterComponentWrapper>
          )}
        </TagFilterTooltip>
      </FormControl>
    </ClickAwayListener>
  ) : null;
};

const TagsFilterComponentWrapper = styled.div<{ customWidth?: number }>`
  height: 100%;
  border: 1px solid ${colors.grey3};
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 7px 7px 7px 14px;
  width: ${(props) => (props.customWidth ? `${props.customWidth}px` : '90px')};
  cursor: pointer;
  &:hover {
    border: 1px solid ${colors.black};
  }
  &.tags-selected {
    background: ${colors.grey4};
    width: auto;
  }
`;

export default TagsFilterComponent;
